import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DashboardSummary, TeamDichotomiesContent } from '../app/app.models';
import { AppService } from './app.service';
import { ApplicationInsightsService } from './application-insights.service';
import { GUIDE_SERVICE } from './guide/tokens';
import { GuideServiceProvider } from './guide/types';
import { ManageTeamService } from './manage/services/manage-team/manage-team.service';
import { HtmlReport } from './report/report.models';
import { ContentService } from './services/content.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { SessionService } from './services/session.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  title = 'MBTIonline | Explore Teams';

  public isUserDetailsLoaded: boolean = false;
  public teamInfoLoaded: boolean = false;
  public isDashboardDataLoaded: boolean = false;
  public initializationError: boolean = false;

  constructor(
    private appService: AppService,
    @Inject(GUIDE_SERVICE) private guideService: GuideServiceProvider,
    private router: Router,
    private applicationInsightsService: ApplicationInsightsService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private contentService: ContentService,
    private _manageTeamService: ManageTeamService,
    private _sessionService: SessionService,
  ) {
    effect(() => {
      if (this._manageTeamService.teamInfoLoaded()) {
        this.teamInfoLoaded = true;
      }
      if (this._manageTeamService.userDetailsLoaded()) {
        this.isUserDetailsLoaded = true;
      }
    });
  }

  ngOnInit() {
    this.googleAnalyticsService.init();

    // Start the session keep-alive process on app load
    // this._sessionService.startSessionKeepAlive().subscribe();

    // this.router.events.subscribe((event) => {
    //   if (event instanceof NavigationEnd) {
    //     const target = new URLSearchParams(location.search).get('target');
    //     if (target) {
    //       const normalizedTarget = target.startsWith('/') ? target.slice(1).toLowerCase() : target.toLowerCase();
    //       if (normalizedTarget === 'manage') {
    //         this.router.navigate(['/manage']);
    //       } else if (normalizedTarget === 'results') {
    //         this.router.navigate(['/results']);
    //       }
    //     }
    //   }
    // });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const urlParams = new URLSearchParams(location.search);
        const target = urlParams.get('target');
        if (target) {
          const normalizedTarget = target.startsWith('/') ? target.slice(1).toLowerCase() : target.toLowerCase();
          const currentPath = this.router.url.split('?')[0].toLowerCase();

          if ((normalizedTarget === 'manage' && currentPath !== '/manage') || (normalizedTarget === 'results' && currentPath !== '/results')) {
            // Remove the target query param after redirect to prevent loops
            urlParams.delete('target');
            const newUrl = `/${normalizedTarget}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
            this.router.navigateByUrl(newUrl);
          }
        }
      }
    });

    // Load all initialization APIs - use combineLatest instead of forkJoin because forkJoin waits for all streams of data to be returned
    // but when we are returning an empty observable it has no data. As a result, if there is no userDetails.mbtiType, then
    // appService.initializeTeam would never run
    combineLatest([
      this.appService.getUserDetails(true).pipe(
        switchMap((userDetails) => {
          if (userDetails.mbtiType !== '----') {
            this.contentService.getDashboardSummary<DashboardSummary>(userDetails.mbtiType).subscribe();

            // Has individual type, so call the API
            return this.contentService.getReport('TeamsIndividualCharacteristics', userDetails.mbtiType);
          } else {
            // No Individual Type, so return empty observable
            return of<HtmlReport>();
          }
        }),
      ),
      this.appService.initializeTeam().pipe(
        switchMap((team) => {
          if (team.teamMbtiType) {
            // Has Team Type, so call API
            return this.contentService.getReport('TeamsTeamCharacteristics', team.teamMbtiType);
          } else {
            // No Team Type, so return empty observable
            return of<HtmlReport>();
          }
        }),
      ),
      this.appService.getSummaryContent('ALL'),
      this.appService.getIntroContent(),
    ]).subscribe({
      error: (err) => {
        this.applicationInsightsService.logException(<Error>err);
        if (err instanceof HttpErrorResponse) {
          this.initializationError = err.status === 401 ? false : true;
        } else {
          this.initializationError = true;
        }
      },
      complete: () => {
        this.teamInfoLoaded = true;
        this.isUserDetailsLoaded = true;
        this.isDashboardDataLoaded = true;
        this.initializationError = false;
      },
    });

    // These APIs are not required for the site to load
    this.contentService.getContent<TeamDichotomiesContent[]>('TeamsPreferencePairs').subscribe();
    if (!sessionStorage.getItem('Dashboard')) {
      this.appService.initializeDashboard().subscribe();
    }

    this.appService.loadScript('../assets/js/course.js');

    this.guideService.getGuideCompleted().subscribe(
      (isGuideComplete) => {
        if (!isGuideComplete) this.router.navigate(['/guide']);
      },
      (error) => {
        this.appService.handleError(error);
      },
    );
  }
}
